import { Injectable, OnDestroy, signal } from '@angular/core';

import { isIosTouchWebKit } from '../util/pointer-capability';

@Injectable({ providedIn: 'root' })
export class KeyboardInsetService implements OnDestroy {
  readonly inset = signal(0);

  private viewport: VisualViewport | null = null;
  private frameId: number | null = null;
  private initialized = false;

  initialize(): void {
    if (this.initialized) {
      return;
    }
    this.initialized = true;

    if (typeof window === 'undefined' || !this.isSupportedIOSWebKit() || !window.visualViewport) {
      // Desktop has no virtual keyboard and Android insets the layout viewport
      // itself, so both keep --keyboard-inset pinned at 0px.
      this.setInset(0);
      return;
    }

    this.viewport = window.visualViewport;
    this.viewport.addEventListener('resize', this.scheduleUpdate, { passive: true });
    this.viewport.addEventListener('scroll', this.scheduleUpdate, { passive: true });
    this.scheduleUpdate();
  }

  ngOnDestroy(): void {
    this.viewport?.removeEventListener('resize', this.scheduleUpdate);
    this.viewport?.removeEventListener('scroll', this.scheduleUpdate);

    if (this.frameId !== null && typeof window !== 'undefined') {
      window.cancelAnimationFrame(this.frameId);
      this.frameId = null;
    }
  }

  private isSupportedIOSWebKit(): boolean {
    return isIosTouchWebKit();
  }

  private readonly scheduleUpdate = (): void => {
    if (this.frameId !== null || typeof window === 'undefined') {
      return;
    }

    this.frameId = window.requestAnimationFrame(() => {
      this.frameId = null;

      if (!this.viewport) {
        return;
      }

      const overlap = Math.max(
        0,
        window.innerHeight - (this.viewport.height + this.viewport.offsetTop),
      );
      this.setInset(overlap);
    });
  };

  private setInset(value: number): void {
    this.inset.set(value);

    if (typeof document !== 'undefined') {
      document.documentElement.style.setProperty('--keyboard-inset', `${value}px`);
    }
  }
}
